// Stencil
import {
  Component,
  Element,
  Event,
  EventEmitter,
  getAssetPath,
  h,
  Host,
  Method,
  Prop,
  State
} from "@stencil/core";

// Other Libraries
import { getIconPath, MercuryBundles } from "@genexus/mercury";

// Custom Imports
import { ChEditCustomEvent } from "@genexus/chameleon-controls-library";
import { RadioGroupItemModel } from "@genexus/chameleon-controls-library/dist/types/components/radio-group/types";
import { config } from "../../../common/config";
import {
  ControlValidation,
  validateControls
} from "../../../common/form-validation";
import { Locale } from "../../../common/locale";
import { FormSubmitResult } from "../../../common/types";
import { GXServerConnectionData } from "../connect-gx-server/connect-gx-server";
import { KBVersion } from "../version-control/common/types";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/form--full",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "components/list-box",
  "components/tooltip",
  "chameleon/scrollbar"
];

@Component({
  tag: "gx-ide-share-kb",
  styleUrl: "share-kb.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/share-kb"]
})
export class GxIdeShareKb {
  #showMoreIcon = getIconPath({
    category: "gemini-tools",
    name: "show-more-horizontal",
    colorType: "on-surface"
  });

  #versionsSelect: RadioGroupItemModel[] = [
    { value: "Trunk", caption: "Trunk Version" },
    { value: "All", caption: "All Versions" },
    { value: "Select", caption: "Select Version" }
  ];

  [key: string]: any;

  #_componentLocale: any;
  #kbVersions: KBVersion[] = [];
  #renderedFirstTime = false;
  #chShortcutsEl: HTMLChShortcutsElement;
  #shortcutsSrc = getAssetPath(
    `./gx-ide-assets/create-kb-from-server/shortcuts.json`
  );
  #selectedKbId: string;

  #versionSelectedTypeEl: HTMLChRadioGroupRenderElement;

  @Element() el: HTMLGxIdeShareKbElement;

  #KBNameEl!: HTMLChEditElement;

  /**
   * State to enable/disable the select versions button
   */
  @State() enableSelectVersionsButton: boolean = false;

  @State() validatableControls = new Map<string, ControlValidation>();

  /**
   * The version selected type
   */
  @State() versionSelectedType: VersionOption;

  /**
   * String value to show the number of versions selected ("N versions selected")
   */
  @State() versionsValueInput: string = "";

  /**
   * Set of data required when executing any of the callbacks that require a connection to the server.
   */
  @Prop() readonly connectionData: GXServerConnectionData;

  /**
   * Callback that is invoked when the dialog is closed
   */
  @Prop() readonly cancelCallback!: () => Promise<void>;

  /**
   * The host reopens the gx-ide-connect-server login modal with the previous data, and closes the current modal
   */
  @Prop() readonly changeConnectionCallback: (
    data: GXServerConnectionData
  ) => Promise<void>;

  /**
   * The name of the KB.
   */
  @Prop({ mutable: true }) kbName: string;

  /**
   * Callback that must be invoked when the Share button is pressed.
   */
  @Prop() readonly shareKBCallback: (
    data: ShareKBData
  ) => Promise<FormSubmitResult>;

  /**
   *  Callback invoked by pressing the ellipsis in the version selector. Receives the KB selected by the user, and displays the version selector dialog. Returns a list of versions chosen by the user.
   */
  @Prop() readonly selectKBVersionsCallback: (
    selectedKB: string
  ) => Promise<KBVersion[]>;

  /**
   * Emitted once just after the component is fully loaded and the first render() occurs
   */
  @Event() componentDidRenderFirstTime: EventEmitter<boolean>;

  componentDidLoad() {
    this.versionSelectedType = this.#versionSelectedTypeEl
      .value as VersionOption;

    this.#initializeValidatableControls();
  }

  componentDidRender() {
    if (!this.#renderedFirstTime) {
      this.componentDidRenderFirstTime.emit(
        this.#_componentLocale.componentName
      );
      this.#renderedFirstTime = true;
    }
  }

  async componentWillLoad() {
    this.#_componentLocale = await Locale.getComponentStrings(this.el);
  }

  /**
   * Suspends or reactivates the shortcuts
   */
  @Method()
  async suspendShortcuts(suspendShortcuts: boolean) {
    if (suspendShortcuts) {
      this.#chShortcutsEl.suspend = true;
    } else {
      this.#chShortcutsEl.suspend = false;
    }
  }

  #cancelHandler = (): void => {
    this.cancelCallback();
  };

  #changeConnectionHandler = async (): Promise<void> => {
    this.changeConnectionCallback({
      authenticationType: this.connectionData.authenticationType,
      serverUrl: this.connectionData.serverUrl,
      userName: this.connectionData.userName,
      userPassword: this.connectionData.userPassword
    });
  };

  #shareKBHandler = async (): Promise<void> => {
    if (this.versionSelectedType !== "Select") {
      this.#kbVersions = [];
    }
    this.shareKBCallback({
      name: this.kbName,
      serverUrl: this.connectionData.serverUrl,
      versionList: this.#kbVersions,
      allVersions: this.versionSelectedType === "All"
    }).then((formSubmitResult: FormSubmitResult) => {
      this.validatableControls = validateControls(
        formSubmitResult,
        this.validatableControls
      );
    });
  };

  // TODO: evaluate if this should be a global helper function.
  // It is used on other dialogs as well.
  #evaluateTooltipRender = (
    controlReference: HTMLElement
  ): HTMLChTooltipElement =>
    this.validatableControls?.get(controlReference?.id)?.hasError &&
    this.validatableControls.get(controlReference.id)?.message && (
      <ch-tooltip
        class="tooltip"
        actionElement={controlReference as unknown as HTMLButtonElement}
        blockAlign={config.tooltipSettings.blockAlign}
        inlineAlign={config.tooltipSettings.inlineAlign}
        delay={config.tooltipSettings.delay}
      >
        {this.validatableControls.get(controlReference.id).message}
      </ch-tooltip>
    );

  #initializeValidatableControls = () => {
    // populate validatableControls with the controls that could have errors.
    const validatableControls: HTMLElement[] = [this.#KBNameEl];
    validatableControls.forEach(validatableControl => {
      if (validatableControl.id) {
        this.validatableControls.set(validatableControl.id, {
          reference: validatableControl,
          hasError: false,
          message: undefined
        });
      }
    });
  };

  #kbNameInputHandler = (event: ChEditCustomEvent<string> | InputEvent) => {
    this.kbName = event.detail as string;
  };

  #selectedVersionHandler = async () => {
    if (this.selectKBVersionsCallback) {
      await this.selectKBVersionsCallback(this.#selectedKbId).then(result => {
        if (result?.length) {
          this.#kbVersions = result.map(kbVersion => ({
            id: kbVersion.id,
            name: kbVersion.name
          }));

          if (result.length === 1 && this.#kbVersions[0].id === 0) {
            this.localKbNameInputValue = this.#selectedKbId;
            this.versionsValueInput =
              this.#_componentLocale.kb.versionsPlaceholder;
          } else if (result.length > 1) {
            this.localKbNameInputValue = this.#selectedKbId;
            this.versionsValueInput = `${result.length.toString()} ${
              this.#_componentLocale.kb.versionSelectedFieldValue
            }`;
          } else {
            this.localKbNameInputValue = `${this.#selectedKbId}_${
              this.#kbVersions[0].name
            }`;
            this.versionsValueInput = `${result[0].name} ${
              this.#_componentLocale.kb.oneVersionSelectedFieldValue
            }`;
          }
        }
      });
    }
  };

  #versionsChangedHandler = (event: CustomEvent<string>) => {
    this.versionSelectedType = event.detail as VersionOption;
    if (this.versionSelectedType === "Select") {
      this.enableSelectVersionsButton = true;
    } else {
      this.enableSelectVersionsButton = false;
    }
  };

  #renderFooter = () => {
    const disableShareKbButton = !this.kbName.length;
    return (
      <footer class="footer dialog-footer-with-border spacing-body-inline spacing-body-block-end">
        <div class="buttons-spacer">
          <button
            // cancel button
            class="button-secondary"
            onClick={this.#cancelHandler}
            part="button button-cancel"
          >
            {this.#_componentLocale.footer.cancel}
          </button>

          <button
            // share kb button
            class="button-primary"
            onClick={!disableShareKbButton && this.#shareKBHandler}
            disabled={disableShareKbButton}
            part="button button-create-kb"
          >
            {this.#_componentLocale.footer.shareKB}
          </button>
        </div>
      </footer>
    );
  };

  render(): void {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts
          src={this.#shortcutsSrc}
          ref={(el: HTMLChShortcutsElement) =>
            (this.#chShortcutsEl = el as HTMLChShortcutsElement)
          }
        ></ch-shortcuts>

        <section class="section">
          <header class="header dialog-header-with-border space-body spacing-body-inline spacing-body-block-start">
            <div class="field field-block">
              <label class="label">
                {this.#_componentLocale.header.serverUrl}
              </label>
              <p>{this.connectionData.serverUrl}</p>
            </div>

            <div class="field field-block">
              <label class="label">{this.#_componentLocale.header.user}</label>
              <p>{this.connectionData.userName}</p>
            </div>

            <button
              id="change-connection-button"
              class="button-tertiary button-change"
              onClick={this.#changeConnectionHandler}
            >
              {this.#_componentLocale.header.change}
            </button>
          </header>

          <section class="main field-group spacing-body">
            <div class="field field-block">
              <label class="label" htmlFor="alias">
                {this.#_componentLocale.kb.aliasLabel}
              </label>
              <ch-edit
                // alias
                class="input"
                id="kb-name"
                value={this.kbName}
                onInput={this.#kbNameInputHandler}
                placeholder={this.#_componentLocale.kb.localKbNamePlaceholder}
                ref={(el: HTMLChEditElement) =>
                  (this.#KBNameEl = el as HTMLChEditElement)
                }
              ></ch-edit>
              {this.#evaluateTooltipRender(this.#KBNameEl)}
            </div>

            <div class="versions-container field-group">
              <ch-radio-group-render
                class="radio-group"
                model={this.#versionsSelect}
                onChange={this.#versionsChangedHandler}
                value={this.#versionsSelect[0].value}
                ref={(el: HTMLChRadioGroupRenderElement) =>
                  (this.#versionSelectedTypeEl =
                    el as HTMLChRadioGroupRenderElement)
                }
              ></ch-radio-group-render>

              {this.versionSelectedType === "Select" && (
                <div class="versions-container__item field-group">
                  <div class="field field-block">
                    <label class="label" htmlFor="control-id-1">
                      {this.#_componentLocale.kb.versionsLabel}
                    </label>
                    <div class="versions-container__input">
                      <ch-edit
                        class="input"
                        readonly
                        placeholder={
                          this.enableSelectVersionsButton
                            ? this.#_componentLocale.kb.versionsPlaceholder
                            : this.#_componentLocale.kb
                                .versionsPlaceholderDisabled
                        }
                        value={this.versionsValueInput}
                      ></ch-edit>
                      <button
                        id="select-version-button"
                        class="button-tertiary button-icon-only"
                        onClick={this.#selectedVersionHandler}
                        disabled={!this.enableSelectVersionsButton}
                      >
                        <ch-image
                          src={this.#showMoreIcon}
                          class="icon-xs"
                        ></ch-image>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>

          {this.#renderFooter()}
        </section>
      </Host>
    );
  }
}

export type KBData = {
  id: string;
  name: string;
};

export type ConnectionResultData = {
  formResult: FormSubmitResult;
  kbs: KBData[];
};

export type ShareKBData = {
  name: string;
  serverUrl: string;
  versionList: KBVersion[];
  allVersions: boolean;
};

export type VersionOption = "Trunk" | "All" | "Select";
